import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Play, Square, Pause, AlertOctagon, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd, fmtPct, fmtTime } from "@/lib/api";
import { StatCard, SectionTitle, Badge } from "@/components/Bits";
import EngineHealthBanner from "@/components/EngineHealthBanner";
import EngineHealthCard from "@/components/EngineHealthCard";
import MarketRegimeCard from "@/components/MarketRegimeCard";

// Map every bot_mode to (a) which `positions.exchange` value belongs to
// it, (b) the operator-facing labels for balance/positions/close-all,
// and (c) the colour tone. Centralising this here means every label,
// counter and CTA on the Dashboard is wired to the same source of truth
// — there is no place left where "Paper" leaks into a live screen.
const MODE_VIEW = {
  paper: {
    exchange: "paper",
    balanceLabel: "Paper Balance",
    positionsLabel: "Open Paper Positions",
    closeAllLabel: "Close All Paper",
    closeAllVariant: "neutral",
    bannerLabel: "PAPER",
    tone: "neutral",
  },
  testnet: {
    exchange: "binance_testnet",
    balanceLabel: "Testnet Balance",
    positionsLabel: "Open Testnet Positions",
    closeAllLabel: "Close All Testnet (n/a)",
    closeAllVariant: "disabled",
    bannerLabel: "TESTNET",
    tone: "amber",
  },
  live_disabled: {
    exchange: null,  // execution disabled — no positions axis
    balanceLabel: "Execution Disabled",
    positionsLabel: "Execution Disabled",
    closeAllLabel: "Close All (n/a)",
    closeAllVariant: "disabled",
    bannerLabel: "EXECUTION DISABLED",
    tone: "amber",
  },
  live_enabled: {
    exchange: "binance_live",
    balanceLabel: "Live Balance (Binance)",
    positionsLabel: "Open Live Positions (Binance)",
    closeAllLabel: "Close All Live Positions",
    closeAllVariant: "live",
    bannerLabel: "BINANCE LIVE",
    tone: "live",
  },
  lbank_live_enabled: {
    exchange: "lbank",
    balanceLabel: "Live Balance (LBank)",
    positionsLabel: "Open Live Positions (LBank)",
    closeAllLabel: "Close All Live Positions",
    closeAllVariant: "live",
    bannerLabel: "LBANK LIVE",
    tone: "live",
  },
};

export default function Dashboard() {
  const { status } = useOutletContext();
  const [allPositions, setAllPositions] = useState([]);
  const [lastSignals, setLastSignals] = useState([]);
  const [activeStrategy, setActiveStrategy] = useState(null);
  // Live exchange snapshot (LBank/Binance Live). When in a live mode,
  // this is the SOURCE OF TRUTH for balance + open positions — Mongo
  // is bypassed because LBank-live execution does not persist state
  // locally. `null` means "not yet loaded"; `{available:false}` means
  // "exchange query failed → UI shows Unavailable / sync issue, NOT
  // a fake zero".
  const [liveSnap, setLiveSnap] = useState(null);

  useEffect(() => {
    const tick = async () => {
      // `Promise.allSettled` (instead of Promise.all) so that a failure
      // on any single endpoint — most commonly a 401 during the brief
      // pre-auth-rehydrate window on a hard refresh — does NOT leave
      // sibling promises rejecting with no handler. Sibling rejections
      // would otherwise fire `unhandledrejection` events that CRA's
      // react-dev-overlay surfaces as a full-page red banner, even
      // though the outer try/catch already swallowed the awaited
      // rejection. Each call's own retry/refresh cycle still runs on
      // the 4 s interval below, so a transient 401 self-heals on the
      // next tick once the Supabase token has been attached.
      const [opRes, slRes, acRes] = await Promise.allSettled([
        api.openPositions(),
        api.signalLogs(5),
        api.getActiveStrategy(),
      ]);
      if (opRes.status === "fulfilled") setAllPositions(opRes.value || []);
      if (slRes.status === "fulfilled") setLastSignals(slRes.value || []);
      if (acRes.status === "fulfilled" && acRes.value) setActiveStrategy(acRes.value);
    };
    tick();
    const id = setInterval(tick, 4000);
    // Refresh immediately when BotSettings saves a new mode so the
    // Dashboard's mode-driven labels and counts re-render in lockstep
    // with the global Layout banner. Without this listener Dashboard
    // would lag up to 4s and could briefly mislabel the active mode.
    const onSaved = () => { tick(); };
    window.addEventListener("bot-settings:saved", onSaved);
    return () => {
      clearInterval(id);
      window.removeEventListener("bot-settings:saved", onSaved);
    };
  }, []);

  // Separate poll loop for the live exchange snapshot — only active
  // when the bot_mode is a live one. Slower cadence (6s) than the
  // generic tick (4s) because the snapshot hits LBank/Binance with a
  // signed request and we want to be gentle with the rate limit.
  // On any failure, we set `available:false` rather than wiping the
  // last-good snapshot, so the UI doesn't flicker between values
  // and "Unavailable" on a transient network hiccup.
  const botModeForSnap = status?.bot_settings?.bot_mode;
  useEffect(() => {
    const isLiveMode =
      botModeForSnap === "live_enabled" ||
      botModeForSnap === "lbank_live_enabled";
    if (!isLiveMode) {
      setLiveSnap(null);
      return undefined;
    }
    let cancelled = false;
    const tickSnap = async () => {
      try {
        const snap = await api.liveSnapshot();
        if (!cancelled) setLiveSnap(snap || { available: false, reason: "empty_response" });
      } catch (e) {
        if (!cancelled) {
          setLiveSnap({
            available: false,
            reason: e?.response?.data?.detail || e?.message || "network_error",
            open_positions: [],
            balance_usdt: null,
          });
        }
      }
    };
    tickSnap();
    const id = setInterval(tickSnap, 6000);
    return () => { cancelled = true; clearInterval(id); };
  }, [botModeForSnap]);

  const action = async (fn, label) => {
    try { await fn(); toast.success(`${label} ok`); }
    catch (e) { toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const botMode = status?.bot_settings?.bot_mode || "paper";
  const view = MODE_VIEW[botMode] || MODE_VIEW.paper;
  const isLive = botMode === "live_enabled" || botMode === "lbank_live_enabled";
  const isPaper = botMode === "paper";
  const isTestnet = botMode === "testnet";
  const phaseOn = !!status?.bot_settings?.validation_phase_enabled;
  const running = status?.runtime?.state === "running";
  const autoExecActive = phaseOn && running && (isTestnet || isLive);

  // Mode-scoped open positions. SOURCE OF TRUTH:
  //   * Live modes (live_enabled, lbank_live_enabled) → `liveSnap.open_positions`
  //     (read DIRECTLY from the exchange — Mongo is empty for LBank
  //     live execution by design and stale for Binance live).
  //   * Paper / Testnet → Mongo `positions` collection filtered by exchange.
  //   * Live mode + snapshot unavailable → empty list (UI separately
  //     surfaces "Unavailable / sync issue").
  const positions = useMemo(() => {
    if (isLive) {
      return Array.isArray(liveSnap?.open_positions) ? liveSnap.open_positions : [];
    }
    if (!view.exchange) return [];
    return allPositions.filter((p) => (p.exchange || "paper") === view.exchange);
  }, [isLive, liveSnap, allPositions, view.exchange]);

  // Mode-scoped balance. SOURCE OF TRUTH:
  //   * Paper → `paper_balance_usdt` from `/api/bot/status`.
  //   * Testnet → `last_total_wallet_balance` cache (probe-time).
  //   * Live → `liveSnap.balance_usdt` (exchange-direct). When the
  //     snapshot is unavailable we render "Unavailable" — NEVER a
  //     fake zero — to honour the dashboard safety contract.
  const balanceText = useMemo(() => {
    if (isPaper) return `$${fmtUsd(status?.paper_balance_usdt || 0)}`;
    if (botMode === "live_disabled") return "—";
    if (isLive) {
      if (liveSnap == null) return "Loading…";
      if (liveSnap.available && liveSnap.balance_usdt !== null && liveSnap.balance_usdt !== undefined) {
        return `$${fmtUsd(Number(liveSnap.balance_usdt))}`;
      }
      return "Unavailable";
    }
    // Testnet path — keep the old probe-cache behaviour.
    const conn = status?.exchange_connection || {};
    const tested = conn.last_tested_exchange;
    const wallet = conn.last_total_wallet_balance;
    if (tested === view.exchange && wallet !== null && wallet !== undefined) {
      return `$${fmtUsd(Number(wallet))}`;
    }
    return "Not refreshed";
  }, [isPaper, isLive, liveSnap, botMode, status, view.exchange]);

  // PnL display — for paper mode this is the Mongo daily_pnl_summary
  // which already aggregates trades; for live/testnet that summary is
  // the same field (it's just bot_settings.bot_mode-agnostic in the
  // backend — every closed trade contributes regardless of source).
  // We keep it but add a "(all modes)" hint when the daily PnL would
  // include trades that don't belong to the current mode.
  const pnl = status?.daily_pnl_usdt || 0;
  const pnlPct = status?.daily_pnl_percent || 0;
  const pnlClass = pnl > 0 ? "long-text" : pnl < 0 ? "short-text" : "text-white";

  // Compute the unrealized PnL of just the current-mode open positions
  // — that's a strict per-mode number we know is correct.
  const modePnl = useMemo(() => {
    return positions.reduce((s, p) => s + (Number(p.unrealized_pnl) || 0), 0);
  }, [positions]);

  // CTA visibility. We hide paper-only CTAs in live modes so a fat-
  // finger click cannot wipe paper positions while you're routed live
  // (and vice versa). The "Close All" button rendered is the one that
  // matches the current mode. Only paper has a server-implemented
  // close-all today; for testnet/live this CTA is disabled with a
  // tooltip pointing at the per-position close action.
  const showPaperCloseAll = isPaper;

  return (
    <div className="p-6 space-y-6">
      {/* Mode-specific top callout (Layout.jsx already shows the global
          banner; this one gives Dashboard-level context for the read-
          only snapshot below — never a duplicate of the global banner). */}
      {isLive ? (
        <div
          data-testid="dashboard-live-banner"
          data-mode={botMode}
          data-engine-path={activeStrategy?.engine_path || "unknown"}
          className="rounded-sm border border-[#00E676]/50 bg-[#00E676]/10 px-4 py-3 flex items-center gap-3"
        >
          <CheckCircle2 size={18} strokeWidth={2} className="text-[#00E676] shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#00E676] uppercase tracking-wider">
              {view.bannerLabel} EXECUTION ACTIVE
            </div>
            <div className="text-xs text-[#A0A0AB] mt-0.5">
              Real orders are routed to{" "}
              <span className="font-mono">{view.exchange}</span>. Numbers below reflect the live exchange snapshot, not paper simulation.
            </div>
            {/* Engine-path chip — surfaces whether the live cycle is
                evaluating the activated Strategy Profile or the legacy
                entry_rules dict. The latter is silently dormant and was
                the root cause of the production "no signals" outage. */}
            {activeStrategy ? (
              <div className="text-[10px] uppercase tracking-widest mt-2 flex items-center gap-2" data-testid="dashboard-engine-path">
                {activeStrategy.profile_engine_enabled ? (
                  <span className="px-2 py-0.5 rounded-sm border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">
                    PROFILE ENGINE: ENABLED · {activeStrategy.profile?.name || "—"}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/15 text-[#FF3B30]">
                    LEGACY ENTRY_RULES ENGINE · activated profile is DORMANT
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : isTestnet && !autoExecActive ? (
        <div data-testid="testnet-banner" className="rounded-sm border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-4 py-3 flex items-center gap-3">
          <AlertOctagon size={18} strokeWidth={2} className="text-[#F59E0B] shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#F59E0B] uppercase tracking-wider">Testnet execution active</div>
            <div className="text-xs text-[#A0A0AB] mt-0.5">
              Real orders hit <span className="font-mono">testnet.binancefuture.com</span> (play money). Binance production orders remain blocked by <span className="font-mono">LIVE_TRADING_ENABLED=false</span>.
              {phaseOn ? null : (
                <> · <a href="/validation" className="text-[#00E676] underline decoration-dotted hover:decoration-solid">Enable Signal Validation</a> to auto-execute.</>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <EngineHealthBanner />
      <EngineHealthCard />
      <MarketRegimeCard />

      <SectionTitle
        sub={
          // Single source of truth for the "active exchange" label is the
          // bot_mode → MODE_VIEW resolver. Previously this read from
          // `exchange_connection.exchange` which is the user's selected
          // dropdown on the Connection page and can diverge from the
          // actual execution exchange (e.g. dropdown still showing
          // BINANCE_LIVE while bot_mode is LBANK_LIVE_ENABLED). The
          // mismatch was misleading on a live screen.
          isLive
            ? `Active exchange: ${view.bannerLabel}  ·  Mode: ${botMode.toUpperCase()}`
            : `Selected exchange: ${(status?.exchange_connection?.exchange || "—").toUpperCase()}  ·  Mode: ${botMode.toUpperCase()}`
        }
        right={
          <div className="flex items-center gap-2">
            <Badge kind={running ? "on" : "off"} testid="bot-state-badge">
              {status?.runtime?.state?.toUpperCase() || "STOPPED"}
            </Badge>
            <Badge kind={status?.kill_switch_enabled ? "warn" : "off"} testid="kill-switch-status">
              KILL {status?.kill_switch_enabled ? "ON" : "OFF"}
            </Badge>
          </div>
        }
      >
        Dashboard
      </SectionTitle>

      {/* Control row */}
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="start-bot-btn"
          onClick={() => action(api.start, "Start bot")}
          className="bg-[#00E676] text-black hover:bg-[#00C865] rounded-sm"
        >
          <Play size={14} strokeWidth={2} className="mr-1.5" /> Start
        </Button>
        <Button
          data-testid="pause-bot-btn"
          variant="outline"
          onClick={() => action(api.pause, "Pause bot")}
          className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"
        >
          <Pause size={14} strokeWidth={1.5} className="mr-1.5" /> Pause
        </Button>
        <Button
          data-testid="stop-bot-btn"
          variant="outline"
          onClick={() => action(api.stop, "Stop bot")}
          className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"
        >
          <Square size={14} strokeWidth={1.5} className="mr-1.5" /> Stop
        </Button>
        <Button
          data-testid="emergency-stop-btn"
          onClick={() => action(api.emergencyStop, "Emergency stop")}
          className="bg-[#FF3B30] text-white hover:bg-[#D8261C] rounded-sm"
        >
          <AlertOctagon size={14} strokeWidth={2} className="mr-1.5" /> Kill Switch
        </Button>
        {/* Close-all CTA — paper-only for now. Hidden in every other
            mode so a misclick cannot wipe a paper sandbox while you're
            actually routed to a live exchange (and vice versa). */}
        {showPaperCloseAll ? (
          <Button
            data-testid="close-all-paper-btn"
            variant="outline"
            onClick={() => action(api.closeAllPaper, "Close all paper positions")}
            className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"
          >
            <X size={14} strokeWidth={1.5} className="mr-1.5" /> {view.closeAllLabel}
          </Button>
        ) : (
          <Button
            data-testid="close-all-mode-btn"
            data-mode={botMode}
            variant="outline"
            disabled
            className="rounded-sm border-border bg-[#1A1A22] opacity-50 cursor-not-allowed"
            title="No bulk close in this mode — close positions individually below."
          >
            {view.closeAllLabel}
          </Button>
        )}
      </div>

      {/* Stat grid — every label and value is mode-driven. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          testid="stat-balance"
          label={view.balanceLabel}
          value={balanceText}
          sublabel={
            isLive && liveSnap && !liveSnap.available
              ? "sync issue — exchange unreachable"
              : undefined
          }
        />
        <StatCard
          testid="stat-daily-pnl"
          label={isLive ? "Daily PnL" : "Daily PnL"}
          value={isLive ? "Unavailable" : `${pnl >= 0 ? "+" : ""}$${fmtUsd(pnl)}`}
          sublabel={isLive ? "not tracked yet for live" : fmtPct(pnlPct)}
          accent={isLive ? undefined : pnlClass}
        />
        <StatCard
          testid="stat-trades-today"
          label="Trades Today"
          value={isLive ? "Unavailable" : (status?.trades_today ?? 0)}
          sublabel={isLive ? "not tracked yet for live" : undefined}
        />
        <StatCard
          testid="stat-open-positions"
          label={isLive ? "Open Live Positions" : (isTestnet ? "Open Testnet Positions" : "Open Paper Positions")}
          value={
            isLive && liveSnap && !liveSnap.available
              ? "Unavailable"
              : positions.length
          }
          sublabel={
            isLive && liveSnap && !liveSnap.available
              ? "sync issue — exchange unreachable"
              : positions.length > 0
                ? `uPnL ${modePnl >= 0 ? "+" : ""}$${fmtUsd(modePnl)}`
                : undefined
          }
          accent={
            isLive && liveSnap && !liveSnap.available
              ? undefined
              : positions.length > 0
                ? (modePnl >= 0 ? "long-text" : "short-text")
                : undefined
          }
        />
      </div>

      {/* Bottom row: open positions (mode-scoped) + recent signals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0F0F14] border border-border rounded-sm" data-testid="dashboard-positions-panel" data-mode={botMode}>
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">{view.positionsLabel}</span>
            <span className="font-mono text-xs text-[#71717A]" data-testid="dashboard-positions-count">{positions.length}</span>
          </div>
          <div className="p-4">
            {isLive && liveSnap?.lbank_unknown_position_warning ? (
              <div
                data-testid="lbank-unknown-warning"
                className="mb-3 px-3 py-2 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs"
              >
                ⚠ {liveSnap.lbank_unknown_position_count} position(s) with
                <b> UNKNOWN side </b>
                detected on LBank. The bot is in <b>fail-closed</b> mode
                for this symbol — no new entries until resolved. Run
                <code className="px-1 mx-1 bg-black/30 rounded">scripts/lbank_dump_positions.py</code>
                on the backend to inspect the raw payload.
              </div>
            ) : null}
            {isLive && liveSnap && !liveSnap.available ? (
              <div data-testid="dashboard-positions-unavailable" className="text-sm text-[#F59E0B]">
                Unavailable / sync issue — could not reach {view.exchange.toUpperCase()}.
                {liveSnap.reason ? <div className="text-[10px] text-[#71717A] mt-1 font-mono">{liveSnap.reason}</div> : null}
              </div>
            ) : positions.length === 0 ? (
              <div className="text-sm text-[#71717A]">
                {botMode === "live_disabled"
                  ? "Execution is disabled — no live or paper positions are tracked here."
                  : isLive
                    ? `No open positions on ${view.exchange}.`
                    : "No open positions."}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
                  <tr><th className="text-left pb-2">Symbol</th><th className="text-left pb-2">Side</th><th className="text-right pb-2">Entry</th><th className="text-right pb-2">Size</th><th className="text-right pb-2">PnL</th></tr>
                </thead>
                <tbody>
                  {positions.map((p, i) => (
                    <tr key={p.id || `${p.symbol}-${p.side}-${i}`} className="border-t border-border/60" data-testid={`live-position-row-${i}`}>
                      <td className="py-2 font-mono text-xs" data-testid={`live-position-symbol-${i}`}>{p.symbol || "—"}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-1.5">
                          <Badge kind={p.side === "LONG" ? "long" : p.side === "SHORT" ? "short" : "warn"} testid={`live-position-side-${i}`}>{p.side || "?"}</Badge>
                          {p.authority_drift_vs_adapter ? (
                            <span
                              data-testid={`live-position-drift-${i}`}
                              className="text-[9px] uppercase tracking-widest text-[#F59E0B] border border-[#F59E0B]/40 rounded-sm px-1 py-[1px] font-mono"
                              title={`Adapter said ${p.side_before_authority || "?"} — Authority overrode to ${p.side}. Source: ${p.authority_source}/${p.authority_confidence}.`}
                            >
                              AUTH↺
                            </span>
                          ) : null}
                          {p.authority_source && p.authority_source !== "LBANK_FRESH" ? (
                            <span
                              data-testid={`live-position-auth-source-${i}`}
                              className="text-[9px] uppercase tracking-widest text-[#71717A] border border-border/60 rounded-sm px-1 py-[1px] font-mono"
                              title={`Authority source: ${p.authority_source} · confidence: ${p.authority_confidence}`}
                            >
                              {p.authority_source.replace("_", " ").toLowerCase()}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="cell-num py-2">${fmtUsd(p.entry_price)}</td>
                      <td className="cell-num py-2">{Number(p.size).toFixed(6)}</td>
                      <td className={`cell-num py-2 ${p.unrealized_pnl >= 0 ? "long-text" : "short-text"}`}>
                        {p.unrealized_pnl >= 0 ? "+" : ""}${fmtUsd(p.unrealized_pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-[#0F0F14] border border-border rounded-sm">
          <div className="px-4 py-3 border-b border-border">
            <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">Recent Signals</span>
          </div>
          <div className="p-4 space-y-2">
            {lastSignals.length === 0 ? (
              <div className="text-sm text-[#71717A]">No signals yet. Start the bot.</div>
            ) : lastSignals.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-3 text-sm border-b border-border/40 pb-2 last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge kind={s.side_evaluated === "LONG" ? "long" : "short"}>{s.side_evaluated}</Badge>
                  <Badge kind={s.decision === "OPEN" ? "on" : s.decision === "BLOCKED" ? "warn" : "off"}>{s.decision}</Badge>
                  <span className="text-[#A0A0AB] truncate">{s.reason}</span>
                </div>
                <span className="font-mono text-xs text-[#71717A] shrink-0">{fmtTime(s.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="text-xs text-[#71717A]">
        Last signal: <span className="font-mono text-[#A0A0AB]">{status?.runtime?.last_signal_decision || "—"}</span>{" · "}
        {status?.runtime?.last_signal_reason || ""}{" · "}
        {fmtTime(status?.runtime?.last_signal_at)}
      </div>
    </div>
  );
}
